import {
  ListChecks,
  Mail,
  MessageCircle,
  MoreVertical,
  Notebook,
  Phone,
  Pin,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import type { TimelineInteractiveCardAction, TimelineItemType } from "../types";
import { Fragment, ReactNode, useMemo, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../tooltip";
import { Badge } from "../../../badge";
import { Button, ButtonProps } from "../../../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../../dropdown-menu";
import { formatDateTime } from "@tellescope/utils";
import { cn } from "../../../../lib/utils";
import { useTranslation } from "react-i18next";

/**
 * Maps timeline item types to their corresponding Lucide icons.
 * Used to automatically display the appropriate icon for each timeline entry type.
 */
const ITEM_ICON_MAP: Record<TimelineItemType, LucideIcon> = {
  phone_call: Phone,
  form_response: ListChecks,
  sms_message: Smartphone,
  chat_room: MessageCircle,
  email: Mail,
  note: Notebook,
};

type PrimaryActionProps =
  | {
      icon?: LucideIcon;
      label: string;
      buttonVariant?: ButtonProps["variant"];
      onClick: () => void;
    }
  | {
      label: string;
      content: ReactNode;
    };

/**
 * Props for the Header component.
 */
export interface HeaderProps {
  /**
   * Icon to display in the header.
   * Can be either a predefined TimelineItemType string (e.g., "phone_call", "email")
   * or a custom Lucide icon component.
   */
  icon: TimelineItemType | LucideIcon;

  /**
   * Whether the item is pinned to the top of the timeline.
   * Shows a filled pin icon when true.
   */
  isPinned?: boolean;

  /**
   * Whether the item has been read by the user.
   * Shows a blue dot indicator when true.
   */
  isUnread?: boolean;

  /**
   * The title/heading text for the timeline item.
   */
  title: string;

  /**
   * Timestamp for when the timeline item occurred.
   * Accepts Date object, ISO string, or Unix timestamp.
   * Displayed as "MMM D, YYYY" format (e.g., "Jan 15, 2024").
   */
  timestamp: Date | string | number;

  /**
   * Optional preview content shown in a hover tooltip.
   * Only displayed when the card is in collapsed state (no children).
   */
  preview?: {
    /**
     * Main preview text content to display in the tooltip.
     */
    content: string;

    /**
     * Optional recent activity timestamp or description.
     * Displayed at the bottom of the preview tooltip.
     */
    recentActivity?: string;
  };

  /**
   * Optional array of tags/labels associated with the timeline item.
   */
  tags?: string[];

  /**
   * Optional actions available for the timeline item.
   * Can be a flat array of actions or grouped arrays for menu sections.
   * Groups are visually separated by dividers in the dropdown menu.
   *
   * @example
   * ```tsx
   * // Flat array - single section
   * actions={[
   *   { icon: EditIcon, label: "Edit", onClick: handleEdit },
   *   { icon: DeleteIcon, label: "Delete", onClick: handleDelete }
   * ]}
   *
   * // Grouped array - multiple sections with dividers
   * actions={[
   *   [
   *     { icon: EditIcon, label: "Edit", onClick: handleEdit },
   *     { icon: DuplicateIcon, label: "Duplicate", onClick: handleDuplicate }
   *   ],
   *   [
   *     { icon: DeleteIcon, label: "Delete", onClick: handleDelete }
   *   ]
   * ]}
   * ```
   */
  actions?: TimelineInteractiveCardAction[] | TimelineInteractiveCardAction[][];

  /**
   * Optional additional CSS classes to apply to the header container.
   */
  className?: string;

  /**
   * Optional configuration for the primary action displayed outside the actions dropdown.
   * If not provided, no button is shown.
   */
  primaryAction?: PrimaryActionProps | PrimaryActionProps[];
}

/**
 * Header component for TimelineInteractiveCard that displays item metadata and actions.
 *
 * This component renders the header section of a timeline card with:
 * - An icon representing the item type (phone, email, note, etc.)
 * - Visual indicators for pinned and unread states
 * - Title and formatted timestamp
 * - Hover-triggered preview tooltip with content, tags, and recent activity
 * - Dropdown menu with configurable actions (supports submenus and grouped actions)
 *
 * The header adapts its appearance based on the card's expanded state:
 * - Collapsed: Shows preview tooltip on hover
 * - Expanded: Highlighted background, no preview tooltip
 *
 * @example
 * ```tsx
 * <Header
 *   icon="phone_call"
 *   isPinned
 *   isUnread
 *   title="Client Call"
 *   timestamp={new Date()}
 *   preview={{
 *     content: "Discussed Q4 goals and budget",
 *     recentActivity: "Last updated 2h ago"
 *   }}
 *   tags={[
 *     { id: "1", name: "Important", color: "#ef4444" },
 *     { id: "2", name: "Follow-up", color: "#3b82f6" }
 *   ]}
 *   actions={[
 *     [
 *       { icon: EditIcon, label: "Edit", onClick: () => {} },
 *       { icon: ShareIcon, label: "Share", onClick: () => {} }
 *     ],
 *     [
 *       { icon: ArchiveIcon, label: "Archive", onClick: () => {} },
 *       { icon: DeleteIcon, label: "Delete", onClick: () => {} }
 *     ]
 *   ]}
 *   primaryAction={{
 *     label: "Collapse",
 *     onClick: () => {},
 *   }}
 * />
 * ```
 */
export const Header = ({
  icon,
  isPinned,
  isUnread,
  title,
  timestamp,
  preview,
  tags,
  actions,
  className,
  primaryAction,
}: HeaderProps) => {
  const { t } = useTranslation("timeline");
  const Icon = typeof icon === "string" ? ITEM_ICON_MAP[icon] : icon;
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  const normalizedPrimaryActions = useMemo(
    () => (primaryAction ? (Array.isArray(primaryAction) ? primaryAction : [primaryAction]) : []),
    [primaryAction]
  );

  const normalizedActions = useMemo<TimelineInteractiveCardAction[][]>(() => {
    if (!actions || actions.length === 0) return [];
    // Type guard: if first element is an array, then actions is 2D
    if (Array.isArray(actions[0])) {
      return actions as TimelineInteractiveCardAction[][];
    }
    return [actions as TimelineInteractiveCardAction[]];
  }, [actions]);

  return (
    <div className={cn("relative flex items-center justify-between px-6 py-4 cursor-pointer", className)}>
      <TooltipProvider>
        <Tooltip open={previewIsOpen} onOpenChange={() => {}}>
          <div className="flex-1 relative">
            <div
              className="flex items-center gap-x-2 max-w-max"
              onMouseEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPreviewIsOpen(true);
              }}
              onMouseLeave={(e) => {
                e.preventDefault();
                e.preventDefault();
                setPreviewIsOpen(false);
              }}
            >
              {!!isPinned && <Pin className="h-4 w-4 text-primary fill-primary" />}
              <Icon className="h-4 w-4 text-gray-900" />
              {!!isUnread && <div className="w-2 h-2 rounded-full bg-blue-500" />}
              <h4 className="text-sm font-medium text-gray-900 truncate max-w-[70%]">{title}</h4>
              <p className="text-sm text-gray-500">{formatDateTime(timestamp, "MMM D, YYYY")}</p>
              <TooltipTrigger
                asChild
                onMouseEnter={(e) => {
                  e.preventDefault();
                }}
                onMouseLeave={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="flex-1 absolute top-0 right-0 bottom-0 invisible" />
              </TooltipTrigger>
            </div>
          </div>

          {!!preview && (
            <TooltipContent
              className="bg-white border border-gray-200 w-80"
              hideArrow
              side="bottom"
              sideOffset={25}
              align="end"
              alignOffset={-65}
            >
              <div className="space-y-3 p-2">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
                  <p className="text-xs text-gray-500 min-w-min text-nowrap">
                    {formatDateTime(timestamp, "MMM D, YYYY")}
                  </p>
                </div>
                <p className="text-sm text-gray-700">{preview.content}</p>
                {!!preview.recentActivity && (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <p className="text-wrap">{preview.recentActivity}</p>
                    <p className="text-nowrap">{t("Recent activity")}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {!!tags?.length &&
                    tags.map((tag, i) => (
                      <Badge
                        key={`${tag}-${i}`}
                        variant="secondary"
                        className="text-xs whitespace-nowrap flex-shrink-0"
                      >
                        {tag}
                      </Badge>
                    ))}
                </div>
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <div className="flex gap-2 items-center ml-4 max-h-[24px]">
        {!!normalizedPrimaryActions.length &&
          normalizedPrimaryActions.map((action) =>
            "content" in action ? (
              action.content
            ) : (
              <Button key={action.label} variant={action.buttonVariant || "outline"} onClick={action.onClick} size="sm">
                {action.icon && <action.icon className="h-2 w-2" />}
                {action.label}
              </Button>
            )
          )}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <MoreVertical className="h-4 w-4 text-gray-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {normalizedActions.map((actionGroup, i) => {
              const isLastGroup = i === normalizedActions.length - 1;
              const shownActionCount = actionGroup.reduce((acc, action) => (action.hidden ? acc : acc + 1), 0);

              return actionGroup.map((action, j) => {
                if (action.hidden) return null;
                const isLastAction = j === shownActionCount - 1;
                const hasSubmenu = "submenu" in action;
                return (
                  <Fragment key={`dropdown-menu-action-${i}-${j}-${action.label}`}>
                    {hasSubmenu ? (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="flex gap-2 items-center">
                          <action.icon className={`h-4 w-4 ${action.fillIcon ? " text-primary fill-primary" : ""}`} />
                          {action.label}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="w-64 p-2">{action.submenu}</DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : (
                      <DropdownMenuItem onClick={action.onClick} className="flex gap-2 items-center">
                        <action.icon className={`h-4 w-4 ${action.fillIcon ? " text-primary fill-primary" : ""}`} />
                        {action.label}
                      </DropdownMenuItem>
                    )}
                    {!isLastGroup && !!isLastAction && <DropdownMenuSeparator />}
                  </Fragment>
                );
              });
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
